import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Trash2, Check, X, Search, Pencil } from 'lucide-react';
import {
  getNutritionFoods, getNutritionDiary,
  addNutritionEntry, updateNutritionEntry, deleteNutritionEntry,
} from '../../api';

// Own copy of the meal list (same convention as the rest of the codebase — each screen keeps
// its own small constant rather than a shared import).
const MEALS = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch',     label: 'Lunch' },
  { id: 'dinner',    label: 'Dinner' },
  { id: 'snack',     label: 'Snacks' },
];

// LOCAL calendar date (not UTC) — same reasoning as PlayerDataContext's tod().
const tod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
function shiftDate(str, delta) {
  const d = new Date(str + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDay(str) {
  return new Date(str + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}

const CARD = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 12 };
const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});

// ── Food search / autocomplete over nutrition_foods ──────────────────────────
function FoodAutocomplete({ onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      try { setResults((await getNutritionFoods(q.trim())) || []); setOpen(true); }
      catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={13} color="var(--text-dimmed)" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search food…"
          style={{ ...inputStyle, paddingLeft: 28 }}
        />
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '105%', left: 0, right: 0, zIndex: 50, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
          {results.map(f => (
            <button key={f.id} type="button"
              onClick={() => { onPick(f); setQ(''); setResults([]); setOpen(false); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              {f.name}
              <span style={{ color: 'var(--text-dimmed)', marginLeft: 6, fontSize: 11 }}>
                {Math.round(f.kcal_per_100g)} kcal/100g{f.grams_per_portion != null ? ` · portion ${Math.round(f.grams_per_portion)} g` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── One logged line ─────────────────────────────────────────────────────────
function EntryRow({ entry, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(String(entry.amount));
  const [unit, setUnit] = useState(entry.unit);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // portion is only offered when the food actually has a portion size on file.
  const canPortion = entry.grams_per_portion != null;

  const save = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) { setErr('Amount must be greater than 0'); return; }
    setBusy(true); setErr('');
    try { await onUpdate(entry.id, { amount: amt, unit }); setEditing(false); }
    catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div style={{ padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{entry.food_name}</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="number" min="0" step="1" value={amount} onChange={e => setAmount(e.target.value)}
            style={{ ...inputStyle, width: 90 }} autoFocus />
          <button style={btn(unit === 'g')} onClick={() => setUnit('g')}>grams</button>
          {canPortion && <button style={btn(unit === 'portion')} onClick={() => setUnit('portion')}>portion</button>}
          <div style={{ flex: 1 }} />
          <button style={btn(true)} onClick={save} disabled={busy}><Check size={13} /></button>
          <button style={btn(false)} onClick={() => { setEditing(false); setErr(''); }}><X size={13} /></button>
        </div>
        {err && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{err}</div>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{entry.food_name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {entry.amount} {entry.unit === 'portion' ? 'portion' : 'g'}
          {entry.unit === 'portion' ? ` · ${entry.grams} g` : ''} · {entry.kcal} kcal ·
          {' '}P {entry.protein_g} / C {entry.carbs_g} / F {entry.fat_g} g
        </div>
      </div>
      <button style={{ ...btn(false), padding: 6 }} onClick={() => { setAmount(String(entry.amount)); setUnit(entry.unit); setEditing(true); }} title="Edit"><Pencil size={12} /></button>
      <button style={{ ...btn(false, '#ef4444'), padding: 6 }} onClick={() => onDelete(entry.id)} title="Delete"><Trash2 size={12} /></button>
    </div>
  );
}

// ── One meal section (Breakfast / Lunch / …) ────────────────────────────────
function MealSection({ meal, entries, onAdd, onUpdate, onDelete }) {
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState(null);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('g');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const rows = entries.filter(e => e.meal_type === meal.id);
  const kcal = rows.reduce((s, e) => s + e.kcal, 0);

  const reset = () => { setAdding(false); setPicked(null); setAmount(''); setUnit('g'); setErr(''); };

  const save = async () => {
    if (!picked) { setErr('Pick a food first'); return; }
    const amt = Number(amount);
    if (!(amt > 0)) { setErr('Enter an amount greater than 0'); return; }
    if (unit === 'portion' && picked.grams_per_portion == null) { setErr('This food has no portion size — use grams'); return; }
    setBusy(true); setErr('');
    try { await onAdd({ meal_type: meal.id, food_id: picked.id, amount: amt, unit }); reset(); }
    catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700 }}>{meal.label}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{kcal} kcal</span>
      </div>

      {rows.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: 'var(--text-dimmed)', marginBottom: 8 }}>Nothing added yet.</div>
      )}
      {rows.map(e => <EntryRow key={e.id} entry={e} onUpdate={onUpdate} onDelete={onDelete} />)}

      {adding ? (
        <div style={{ padding: '10px', background: 'var(--bg-app)', borderRadius: 8, marginTop: 4 }}>
          {!picked ? (
            <FoodAutocomplete onPick={f => { setPicked(f); setUnit('g'); }} />
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                {picked.name}
                <button onClick={() => setPicked(null)} style={{ ...btn(false), padding: '2px 8px', marginLeft: 8, fontSize: 11 }}>change</button>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="number" min="0" step="1" value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="amount" style={{ ...inputStyle, width: 110 }} autoFocus />
                <button style={btn(unit === 'g')} onClick={() => setUnit('g')}>grams</button>
                {picked.grams_per_portion != null && (
                  <button style={btn(unit === 'portion')} onClick={() => setUnit('portion')}>
                    portion ({Math.round(picked.grams_per_portion)} g)
                  </button>
                )}
              </div>
            </>
          )}
          {err && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 6 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={btn(true)} onClick={save} disabled={busy || !picked}>{busy ? 'Saving…' : 'Add'}</button>
            <button style={btn(false)} onClick={reset}>Cancel</button>
          </div>
        </div>
      ) : (
        <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }} onClick={() => setAdding(true)}>
          <Plus size={13} /> Add
        </button>
      )}
    </div>
  );
}

// ── Day total vs goal ───────────────────────────────────────────────────────
function GoalBar({ label, value, goal, unit }) {
  const pct = goal ? Math.round((value / goal) * 100) : null;
  const width = pct == null ? 0 : Math.min(100, pct);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ color: 'var(--text-muted)' }}>
          {value}{unit}{goal != null ? ` / ${goal}${unit}` : ''}{pct != null ? ` · ${pct}%` : ''}
        </span>
      </div>
      {goal != null && (
        <div style={{ height: 6, background: 'var(--gauge-track)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${width}%`, background: pct >= 100 ? '#10b981' : '#3b82f6', borderRadius: 99 }} />
        </div>
      )}
    </div>
  );
}

export default function PlayerNutrition({ playerRecord }) {
  const navigate = useNavigate();
  const [date, setDate] = useState(tod());
  const [data, setData] = useState({
    entries: [], totals: { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
    goal: null,
    effectiveGoals: { kcal_goal: null, protein_g_goal: null, carbs_g_goal: null, fat_g_goal: null },
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!playerRecord?.id) { setLoading(false); return; }
    setLoading(true);
    try { setData(await getNutritionDiary(playerRecord.id, date)); }
    catch { /* leave last-loaded data in place */ }
    finally { setLoading(false); }
  }, [playerRecord?.id, date]);

  useEffect(() => { load(); }, [load]);

  const addEntry = async (payload) => {
    await addNutritionEntry({ player_id: playerRecord.id, entry_date: date, ...payload });
    load();
  };
  const updateEntry = async (id, patch) => { await updateNutritionEntry(id, patch); load(); };
  const removeEntry = async (id) => { await deleteNutritionEntry(id); load(); };

  const { totals } = data;
  const eg = data.effectiveGoals || {};

  return (
    <div style={{ padding: '14px 14px 24px', maxWidth: 640, margin: '0 auto' }}>
      <button onClick={() => navigate('/player/forms')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0, fontFamily: 'inherit' }}>
        <ChevronLeft size={16} /> Back
      </button>

      <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>Food Tracker</div>

      {/* Date picker */}
      <div style={{ ...CARD, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(d => shiftDate(d, -1))}><ChevronLeft size={15} /></button>
        <input type="date" value={date} onChange={e => setDate(e.target.value || tod())}
          style={{ ...inputStyle, width: 'auto', flex: 1, textAlign: 'center' }} />
        <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(d => shiftDate(d, 1))}><ChevronRight size={15} /></button>
        <button style={btn(date === tod())} onClick={() => setDate(tod())}>Today</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-dimmed)', margin: '-4px 0 12px 2px' }}>{fmtDay(date)}</div>

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <>
          {MEALS.map(m => (
            <MealSection key={m.id} meal={m} entries={data.entries}
              onAdd={addEntry} onUpdate={updateEntry} onDelete={removeEntry} />
          ))}

          {/* Day total */}
          <div style={{ ...CARD, marginTop: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Day total</div>
            <GoalBar label="Calories" value={totals.kcal} goal={eg.kcal_goal} unit=" kcal" />
            <GoalBar label="Protein"  value={totals.protein_g} goal={eg.protein_g_goal} unit=" g" />
            <GoalBar label="Carbs"    value={totals.carbs_g} goal={eg.carbs_g_goal} unit=" g" />
            <GoalBar label="Fat"      value={totals.fat_g} goal={eg.fat_g_goal} unit=" g" />
            {eg.kcal_goal == null && eg.fat_g_goal == null && (
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 4 }}>
                No calorie / fat goal set by staff yet.
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>
              Protein &amp; carb goals are calculated from your body weight and the day type in
              the week schedule.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
